/**
 * Drives a Help pane tour (Help pane live tours,
 * design/2026-10-14-help-agent-tours.md). The state machine is
 * help/tourRuntime; this hook does what each state asks of the page:
 *
 * - Entering a step (Start, Next): go to its page when it is on another one
 *   and wait for that route to mount, run its action through the mounted
 *   page's own tools (help/tourActions: demo-safe tools only, picks resolved
 *   against the page) or, for a walkthrough step with no page tool, click or
 *   type into a control by its label (help/walkthroughActions), outline its
 *   control, and ask the help route to
 *   explain it: one model call per step.
 * - Back: land on the previous step, outline and explain it again. Its
 *   action does not run again; nothing is undone.
 *
 * The page's tools and its tree load after the route mounts, so an action
 * whose tool is not on the page yet, or whose pick finds nothing yet, is
 * tried again a few times before the pane says it could not.
 *
 * Any caller may start a run: the pane's Show me around menu now, a voice
 * provider later (useHelp().startTour), with the same page registry.
 */
import { useEffect, useRef, useState } from 'react'
import type { VoiceUITool } from '../voice/types'
import type { NavigateResult } from '../pageTools/PageToolsProvider'
import { clearOutline, outlineControl } from './outline'
import { runTourAction } from './tourActions'
import { runDomAction } from './walkthroughActions'
import { NO_TOUR, currentStep, stepOnScreen, tourReducer } from './tourRuntime'
import type { TourEvent, TourRun, TourState, TourStep } from './tourRuntime'

/** How long a page has to settle after it mounts before its step is shown. */
export const ARRIVAL_SETTLE_MS = 400
/** A control or a page tool that loads after the page mounts is tried again this often. */
export const OUTLINE_RETRY_MS = 400
export const OUTLINE_TRIES = 8
/** After an action, how long the page has to redraw (and re-register its tools and context) before the step is explained. */
export const ACTION_SETTLE_MS = 250
/** A step waiting for a route no navigation reaches (an action on the step before should have) explains from here after this. */
export const ROUTE_WAIT_MS = 8000

export interface UseTourRunnerOptions {
  /** The mounted page's tools, read live. */
  tools: () => VoiceUITool[]
  /** Go to a NAV_PAGES page; null when there is no voice provider to do it. */
  navigateToPage: (page: string) => NavigateResult | null
  /** The route name on screen. */
  routeName: string
  /** The mounted page's registration key: it changes when a page (re)registers its tools. */
  pageKey?: string
  /** Ask the help route to explain the step; the line is shown as the person's. */
  explain: (line: string) => Promise<void>
  /** Add a line from the pane itself. */
  note: (line: string) => void
  /** The words that name a step in its explain line (its feature's name, control or say). */
  describe: (step: TourStep) => string
}

export interface TourRunner {
  state: TourState
  /** The state as of now, for callers that read it outside a render (the help route's tour context). */
  current: () => TourState
  /** `from.index`: resume a run at this step, shown without running its action (or any before it) again. */
  start: (run: TourRun, from?: { index: number }) => void
  next: () => void
  back: () => void
  end: () => void
  /** The person asked a question mid-step: the step stays. */
  ask: () => void
  /** Their question was answered. */
  answered: () => void
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export function useTourRunner(options: UseTourRunnerOptions): TourRunner {
  const opts = useRef(options)
  opts.current = options

  const [state, setState] = useState<TourState>(NO_TOUR)
  const stateRef = useRef(state)
  // Bumped on every move: async work for an older step stops when it changes.
  const moveRef = useRef(0)
  // Whether the step being waited for runs its action on arrival (not on Back).
  const actRef = useRef(false)
  // Steps whose action already ran in this run: Back then Next re-shows such a
  // step without doing it again (no second variable added to an axis).
  const actedRef = useRef(new Set<number>())
  const outlineTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const routeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const dispatch = (event: TourEvent): TourState => {
    const next = tourReducer(stateRef.current, event)
    stateRef.current = next
    setState(next)
    return next
  }

  const clearTimers = () => {
    if (outlineTimer.current) clearTimeout(outlineTimer.current)
    if (routeTimer.current) clearTimeout(routeTimer.current)
    outlineTimer.current = null
    routeTimer.current = null
  }

  const stopOutline = () => {
    clearTimers()
    clearOutline()
  }

  // Ring the step's control, trying again while the page loads it.
  const outlineStep = (move: number, step: TourStep) => {
    const control = step.control
    if (!control) return
    let tries = 0
    const attempt = () => {
      outlineTimer.current = null
      if (moveRef.current !== move) return
      if (!outlineControl(control).ok && ++tries < OUTLINE_TRIES) outlineTimer.current = setTimeout(attempt, OUTLINE_RETRY_MS)
    }
    attempt()
  }

  // Run the step's action (a page tool, else a walkthrough's click or fill
  // on the page), trying again while the page is still loading.
  const act = async (move: number, step: TourStep): Promise<{ did?: string; error?: string }> => {
    const action = step.action
    if (!action && !step.click && !step.fill) return {}
    for (let tries = 1; ; tries++) {
      if (moveRef.current !== move) return {}
      const r = action ? await runTourAction(action, opts.current.tools(), stateRef.current.picked) : await runDomAction(step)
      if (moveRef.current !== move) return {}
      if ('did' in r) {
        for (const [name, value] of Object.entries(r.picked)) {
          if (stateRef.current.picked[name] !== value) dispatch({ type: 'picked', name, value })
        }
        dispatch({ type: 'acted', did: r.did })
        return { did: r.did }
      }
      if (!r.retry || tries >= OUTLINE_TRIES) return { error: r.error }
      await sleep(OUTLINE_RETRY_MS)
    }
  }

  // The step is on screen: do its action (when entered forwards), show its
  // control and have it explained.
  const showStep = async (entered: TourState, runAction: boolean) => {
    const step = currentStep(entered)
    if (!entered.tour || !step) return
    const move = moveRef.current
    const fresh = runAction && !actedRef.current.has(entered.index)
    const { did, error } = fresh ? await act(move, step) : {}
    if (did) actedRef.current.add(entered.index)
    if (did) await sleep(ACTION_SETTLE_MS)
    if (moveRef.current !== move) return
    if (error) opts.current.note(`I could not do that here: ${error}.`)
    outlineStep(move, step)
    const head = `Step ${entered.index + 1} of ${entered.tour.steps.length}: ${opts.current.describe(step)}`
    void opts.current.explain(did ? `${head}. I just did: ${did}` : head)
  }

  const arrive = () => void showStep(dispatch({ type: 'arrived' }), actRef.current)

  // Land on a step: show it here, or go to its page and wait for it to mount.
  const enterStep = (entered: TourState, runAction: boolean) => {
    moveRef.current++
    stopOutline()
    const step = currentStep(entered)
    if (!step) return
    actRef.current = runAction
    if (entered.phase === 'explaining') {
      void showStep(entered, runAction)
      return
    }
    const moved = step.page ? opts.current.navigateToPage(step.page) : null
    if (moved && !moved.ok) {
      // Cannot get there (no job chosen, say): explain it from here.
      opts.current.note(`I could not open that page: ${moved.error}.`)
      arrive()
      return
    }
    if (!moved && !step.route) {
      arrive()
      return
    }
    // Wait for the route (the arrival effect below). A step whose route only
    // an earlier action reaches (Edit Spec) stops waiting after a while.
    const move = moveRef.current
    routeTimer.current = setTimeout(() => {
      routeTimer.current = null
      if (moveRef.current !== move || stateRef.current.phase !== 'navigating') return
      opts.current.note('I could not open that page from here.')
      arrive()
    }, ROUTE_WAIT_MS)
  }

  // A step on another page: once the route is the step's and the page has
  // mounted (its registration settles), show the step. Re-armed by each
  // route or registration change, so a page that mounts in stages waits.
  useEffect(() => {
    const now = stateRef.current
    if (now.phase !== 'navigating' || !stepOnScreen(currentStep(now), options.routeName)) return
    const timer = setTimeout(() => {
      if (routeTimer.current) clearTimeout(routeTimer.current)
      routeTimer.current = null
      arrive()
    }, ARRIVAL_SETTLE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.routeName, options.pageKey])

  // Unmount: no timer outlives the pane's provider.
  useEffect(() => () => {
    moveRef.current++
    clearTimers()
  }, [])

  return {
    state,
    current: () => stateRef.current,
    start: (run, from) => {
      const entered = dispatch({ type: 'start', tour: run, here: opts.current.routeName, index: from?.index })
      actedRef.current = new Set(from ? Array.from({ length: entered.index + 1 }, (_, i) => i) : [])
      enterStep(entered, !from)
    },
    next: () => enterStep(dispatch({ type: 'next', here: opts.current.routeName }), true),
    back: () => enterStep(dispatch({ type: 'back', here: opts.current.routeName }), false),
    end: () => {
      moveRef.current++
      stopOutline()
      dispatch({ type: 'close' })
    },
    ask: () => void dispatch({ type: 'ask' }),
    answered: () => void dispatch({ type: 'answered' }),
  }
}

export default useTourRunner
